import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Edit, Trash2, Search, Loader2, Package } from 'lucide-react';
import { produtoServicoService, ProdutoServico } from '../../../services/propostaService';

interface ProdutoServicoManagementProps {
  onBack: () => void;
}

export function ProdutoServicoManagement({ onBack }: ProdutoServicoManagementProps) {
  const [produtos, setProdutos] = useState<ProdutoServico[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduto, setEditingProduto] = useState<ProdutoServico | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    tipo: 'servico' as 'produto' | 'servico',
    codigo: '',
    nome: '',
    descricao: '',
    valorUnit: 0,
    unidade: 'un',
  });

  useEffect(() => {
    loadProdutos();
  }, [tipoFilter]);

  const loadProdutos = async () => {
    setIsLoading(true);
    try {
      const data = await produtoServicoService.list({ tipo: tipoFilter || undefined, active: true });
      setProdutos(data);
    } catch (error) {
      console.error('Erro ao carregar:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProduto) {
        await produtoServicoService.update(editingProduto.id, formData);
      } else {
        await produtoServicoService.create(formData);
      }
      setShowForm(false);
      setEditingProduto(null);
      resetForm();
      loadProdutos();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar');
    }
  };

  const handleEdit = (produto: ProdutoServico) => {
    setEditingProduto(produto);
    setFormData({
      tipo: produto.tipo as 'produto' | 'servico',
      codigo: produto.codigo || '',
      nome: produto.nome,
      descricao: produto.descricao || '',
      valorUnit: Number(produto.valorUnit),
      unidade: produto.unidade,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Deseja desativar este item?')) return;
    try {
      await produtoServicoService.delete(id);
      loadProdutos();
    } catch (error) {
      console.error('Erro ao excluir:', error);
    }
  };

  const resetForm = () => {
    setFormData({ tipo: 'servico', codigo: '', nome: '', descricao: '', valorUnit: 0, unidade: 'un' });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const filteredProdutos = produtos.filter(p =>
    p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Voltar para Propostas
        </button>
        <h2 className="text-xl font-bold">Produtos e Serviços</h2>
        <button onClick={() => { resetForm(); setEditingProduto(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">
          <Plus className="w-4 h-4" /> Novo
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" />
        </div>
        <select value={tipoFilter} onChange={e => setTipoFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white">
          <option value="">Todos</option>
          <option value="produto">Produtos</option>
          <option value="servico">Serviços</option>
        </select>
      </div>


      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-bold mb-4">{editingProduto ? 'Editar' : 'Novo'} Produto/Serviço</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select value={formData.tipo} onChange={e => setFormData({ ...formData, tipo: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white">
                    <option value="servico">Serviço</option>
                    <option value="produto">Produto</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                  <input type="text" value={formData.codigo} onChange={e => setFormData({ ...formData, codigo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" placeholder="SKU-001" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input type="text" required value={formData.nome} onChange={e => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" placeholder="Nome do produto/serviço" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea value={formData.descricao} onChange={e => setFormData({ ...formData, descricao: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" rows={3} placeholder="Descrição detalhada..." />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor Unitário *</label>
                  <input type="number" required min="0" step="0.01" value={formData.valorUnit}
                    onChange={e => setFormData({ ...formData, valorUnit: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                  <select value={formData.unidade} onChange={e => setFormData({ ...formData, unidade: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white">
                    <option value="un">Unidade (un)</option>
                    <option value="hr">Hora (hr)</option>
                    <option value="m²">Metro² (m²)</option>
                    <option value="m">Metro (m)</option>
                    <option value="kg">Quilo (kg)</option>
                    <option value="pç">Peça (pç)</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={() => { setShowForm(false); setEditingProduto(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
          </div>
        ) : filteredProdutos.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nenhum item cadastrado</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Unidade</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredProdutos.map(produto => (
                <tr key={produto.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600">{produto.codigo || '-'}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{produto.nome}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${produto.tipo === 'produto' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                      {produto.tipo === 'produto' ? 'Produto' : 'Serviço'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">{formatCurrency(Number(produto.valorUnit))}</td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600">{produto.unidade}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(produto)} className="p-2 hover:bg-gray-100 rounded" title="Editar">
                        <Edit className="w-4 h-4 text-blue-600" />
                      </button>
                      <button onClick={() => handleDelete(produto.id)} className="p-2 hover:bg-gray-100 rounded" title="Excluir">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
